import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useSession } from '../context/Session';
import { useT } from '../i18n';
import { Empty, Icon, Modal, useCelebrate, useConfirm, useToast } from '../components/ui';
import {
  BusyButton,
  CalorieFlameBar,
  DEFAULT_GOALS,
  MacroRings,
  MealCard,
  MealDetailModal,
  PeriodTabs,
  WaterTrackerCard,
  WeeklyCaloriesCard,
  WeeklyWaterCard,
} from '../components/nutrition';
import { useDoc, useToday } from '../lib/hooks';
import { dateKey, formatDate, parseDateKey, weekDates } from '../lib/dates';
import {
  addAiEntry,
  addQuickAddEntry,
  deleteNutritionEntry,
  macrosOf,
  mergeProfileAgeHeightIfMissing,
  nutritionDayRef,
  recordNutritionEstimateToHistory,
  restoreNutritionEntry,
  updateMacroTargets,
  waterDayRef,
} from '../lib/services';
import { AI_KIND, allowanceBlockedMessage, allowanceFromUserData, getAiAllowance } from '../lib/allowance';
import {
  MAX_ADD_ITEM_CHARS,
  MAX_ADD_ITEMS,
  addPhotoMealItems,
  getMacroTargets,
  getNutritionEstimate,
  getNutritionEstimateFromPhoto,
} from '../lib/cloud';
import { useBodyDays, useNutritionDays, useProgram, useWaterDays } from '../lib/useUserData';

const MEAL_TYPES = ['breakfast', 'lunch', 'dinner', 'snack'];

// Same windows as the app's _defaultMealTypeForNow: gaps between main meals
// (afternoon, late night) count as snack.
function defaultMealType() {
  const h = new Date().getHours();
  if (h >= 5 && h < 11) return 'breakfast';
  if (h >= 11 && h < 15) return 'lunch';
  if (h >= 18 && h < 22) return 'dinner';
  return 'snack';
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1]);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

/** Downscales large photos before upload (the app sends camera JPEGs ≈ 1–2 MB). */
async function compressImage(file) {
  if (!file.type.startsWith('image/') || file.size < 1.5 * 1024 * 1024) {
    return { base64: await readFileAsBase64(file), mime: file.type || 'image/jpeg' };
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = url;
    });
    const scale = Math.min(1, 1600 / Math.max(img.width, img.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    return { base64: dataUrl.split(',')[1], mime: 'image/jpeg' };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/* ───────── Photo estimate review ───────── */
const parseAmount = (v) => Number(String(v).trim().replace(',', '.'));
const fmtAmount = (v) => (Number.isInteger(v) ? String(v) : v.toFixed(1));
const isScalable = (item) => item.amount > 0 && !!item.unit;

/** [item] at the typed amount, macros scaled from the original estimate. */
function scaledItem(item, amountText) {
  const a = parseAmount(amountText);
  if (!isScalable(item) || !(a > 0) || a === item.amount) return item;
  const r = a / item.amount;
  return {
    ...item,
    amount: a,
    quantity: `${fmtAmount(a)} ${item.unit}`,
    quantityEstimated: false,
    caloriesKcal: Math.round(item.caloriesKcal * r),
    proteinG: Math.round(item.proteinG * r),
    carbsG: Math.round(item.carbsG * r),
    fatG: Math.round(item.fatG * r),
  };
}

function sumTotals(items) {
  const sum = (k) => items.reduce((total, i) => total + i[k], 0);
  return { caloriesKcal: sum('caloriesKcal'), proteinG: sum('proteinG'), carbsG: sum('carbsG'), fatG: sum('fatG') };
}

const macroLine = (m) => `${m.caloriesKcal} kcal · P ${m.proteinG} g · C ${m.carbsG} g · F ${m.fatG} g`;

let reviewRowId = 0;
const toRow = (item) => {
  reviewRowId += 1;
  return { id: reviewRowId, original: item, amountText: isScalable(item) ? fmtAmount(item.amount) : '' };
};

/**
 * Photo estimate before saving (mobile: meal_photo_review_sheet.dart): amounts
 * are editable (macros scale here, no AI call), wrong items can be removed and
 * missed foods added with one text estimate paid by estimate.addItemToken.
 */
function MealPhotoReviewModal({ estimate, mealType, onSave, onDiscard }) {
  const t = useT();
  const confirm = useConfirm();
  const [rows, setRows] = useState(() => estimate.items.map(toRow));
  const [pending, setPending] = useState([]);
  const [input, setInput] = useState('');
  const [adding, setAdding] = useState(false);
  const [addUsed, setAddUsed] = useState(false);
  const [addError, setAddError] = useState(null);
  const [saving, setSaving] = useState(false);

  const items = rows.map((r) => scaledItem(r.original, r.amountText));
  const totals = sumTotals(items);
  const rowValid = (r) => !isScalable(r.original) || parseAmount(r.amountText) > 0;
  const canSave = rows.length > 0 && rows.every(rowValid) && !adding && !saving;
  const typed = input.trim();
  const foodsToSend = [...pending, ...(typed ? [typed] : [])].slice(0, MAX_ADD_ITEMS);
  const canQueue = !!typed && pending.length < MAX_ADD_ITEMS - 1 && !adding;

  const setAmount = (id, amountText) => setRows((l) => l.map((r) => (r.id === id ? { ...r, amountText } : r)));

  const queue = () => {
    if (!canQueue) return;
    setPending((l) => [...l, typed]);
    setInput('');
  };

  const send = async () => {
    if (!estimate.addItemToken || !foodsToSend.length || adding) return;
    setAdding(true);
    setAddError(null);
    const { estimate: added, grantUsed } = await addPhotoMealItems(mealType, foodsToSend, estimate.addItemToken);
    setAdding(false);
    if (!added) {
      // The server gives the grant back when the AI call failed.
      setAddUsed(grantUsed);
      if (!grantUsed) setAddError(t('nutriAiError'));
    } else if (!added.items.length) {
      setAddUsed(true);
      setAddError(t('nutriAiNoFoodDetected'));
    } else {
      setAddUsed(true);
      setRows((l) => [...l, ...added.items.map(toRow)]);
      setPending([]);
      setInput('');
    }
  };

  // Escape reaches this modal too while the confirm dialog is open.
  const confirming = useRef(false);
  const discard = async () => {
    if (adding || saving || confirming.current) return;
    confirming.current = true;
    const ok = await confirm({
      message: t('mealReviewDiscardConfirm'),
      confirmLabel: t('mealReviewDiscard'),
      cancelLabel: t('mealReviewKeepEditing'),
      danger: true,
    });
    confirming.current = false;
    if (ok) onDiscard();
  };

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await onSave({ ...estimate, items, mealTotals: totals, addItemToken: null });
    } finally {
      setSaving(false);
    }
  };

  const unitLabel = (unit) => (unit === 'adet' || unit === 'piece' ? t('mealReviewUnitPiece') : unit);
  const danger = 'var(--gm-danger, #d32f2f)';

  return (
    <Modal
      open
      onClose={discard}
      closeOnScrim={false}
      title={t('mealReviewTitle')}
      footer={(
        <>
          <button type="button" className="gm-btn gm-btn--ghost" onClick={discard} disabled={adding || saving}>{t('mealReviewDiscard')}</button>
          <BusyButton busy={saving} disabled={!canSave} onClick={save}>{t('mealReviewSave')}</BusyButton>
        </>
      )}
    >
      <p className="gm-muted gm-small" style={{ marginBottom: 14 }}>{t('mealReviewSubtitle')}</p>
      <div className="gm-stack-sm">
        {rows.map((r, idx) => {
          const item = items[idx];
          return (
            <div key={r.id} className="gm-row" style={{ alignItems: 'center', gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="gm-strong">{item.foodName}</div>
                <div className="gm-tiny gm-muted">
                  {isScalable(r.original) ? macroLine(item) : `${item.quantity} · ${macroLine(item)}`}
                </div>
              </div>
              {isScalable(r.original) && (
                <>
                  <input
                    className="gm-input"
                    style={{ width: 64, textAlign: 'center', ...(rowValid(r) ? {} : { borderColor: danger }) }}
                    inputMode="decimal"
                    value={r.amountText}
                    onChange={(e) => setAmount(r.id, e.target.value.replace(/[^0-9.,]/g, ''))}
                  />
                  <span className="gm-small" style={{ maxWidth: 90 }}>{unitLabel(r.original.unit)}</span>
                </>
              )}
              <button
                type="button"
                className="gm-icon-btn gm-icon-btn--sm"
                onClick={() => setRows((l) => l.filter((x) => x.id !== r.id))}
                aria-label={t('mealReviewRemoveItem')}
                title={t('mealReviewRemoveItem')}
              >
                <Icon name="delete" />
              </button>
            </div>
          );
        })}
      </div>

      {estimate.addItemToken && (
        <div className="gm-field" style={{ marginTop: 18 }}>
          <label>{t('mealReviewAddItemTitle')}</label>
          {addUsed ? (
            <span className="gm-tiny gm-muted">{t('mealReviewAddItemUsed')}</span>
          ) : (
            <>
              {pending.length > 0 && (
                <div className="gm-chips" style={{ marginBottom: 8 }}>
                  {pending.map((food, k) => (
                    <button
                      key={`${k}-${food}`}
                      type="button"
                      className="gm-chip selected"
                      disabled={adding}
                      onClick={() => setPending((l) => l.filter((_, j) => j !== k))}
                    >
                      {food} <Icon name="close" size={16} />
                    </button>
                  ))}
                </div>
              )}
              <div className="gm-row" style={{ gap: 8 }}>
                <input
                  className="gm-input"
                  style={{ flex: 1 }}
                  value={input}
                  maxLength={MAX_ADD_ITEM_CHARS}
                  disabled={adding}
                  placeholder={t('mealReviewAddItemHint')}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      queue();
                    }
                  }}
                />
                <button
                  type="button"
                  className="gm-icon-btn"
                  onClick={queue}
                  disabled={!canQueue}
                  aria-label={t('mealReviewAddItemToList')}
                  title={t('mealReviewAddItemToList')}
                >
                  <Icon name="add" />
                </button>
              </div>
              <BusyButton
                busy={adding}
                className="gm-btn gm-btn--soft"
                style={{ marginTop: 8, width: '100%' }}
                disabled={!foodsToSend.length || adding}
                onClick={send}
              >
                <Icon name="auto_awesome" size={18} /> {adding ? t('estimating') : t('mealReviewAddItemButton', foodsToSend.length)}
              </BusyButton>
              <span className="gm-tiny gm-muted">{t('mealReviewAddItemInfo')}</span>
            </>
          )}
          {addError && <span className="gm-tiny" style={{ color: danger }}>{addError}</span>}
        </div>
      )}

      <div style={{ borderTop: '1px solid var(--gm-border, rgba(127,127,127,.25))', marginTop: 18, paddingTop: 12 }}>
        <div className="gm-small gm-muted">{t('mealTotals')}</div>
        <div className="gm-strong">{macroLine(totals)}</div>
      </div>
    </Modal>
  );
}

/* ───────── Add meal ───────── */
function AllowanceLine({ user }) {
  const t = useT();
  const s = allowanceFromUserData(user, AI_KIND.nutrition);
  if (!s.isVisibleToUser) return null;
  return (
    <div className={`gm-banner${s.canUse ? '' : ' gm-banner--warn'}`} style={{ marginTop: 12 }}>
      <Icon name="auto_awesome" size={18} />
      <span>{s.canUse ? t('allowanceNutritionRemaining', s.remaining) : allowanceBlockedMessage(t, s)}</span>
    </div>
  );
}

function AddMealModal({ user, onClose, onSaved, onPremium }) {
  const t = useT();
  const toast = useToast();
  const [mode, setMode] = useState('text');
  const [mealType, setMealType] = useState(defaultMealType);
  const [date, setDate] = useState(dateKey());
  const [text, setText] = useState('');
  const [photo, setPhoto] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [quick, setQuick] = useState({ label: '', kcal: '', p: '', c: '', f: '' });
  const [busy, setBusy] = useState(false);
  const [review, setReview] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => () => photoUrl && URL.revokeObjectURL(photoUrl), [photoUrl]);

  const logDate = parseDateKey(date) || new Date();

  const pickPhoto = (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error(t('photoTooLarge'));
      return;
    }
    setPhoto(file);
    setPhotoUrl(URL.createObjectURL(file));
  };

  const submitAi = async () => {
    if (mode === 'text' && !text.trim()) {
      toast.warning(t('enterMealDescription'));
      return;
    }
    if (mode === 'photo' && !photo) {
      toast.warning(t('pickPhoto'));
      return;
    }
    if (!navigator.onLine) {
      toast.error(t('nutriAiInternetRequired'));
      return;
    }
    setBusy(true);
    try {
      const allowance = await getAiAllowance(user.id, mode === 'photo' ? AI_KIND.nutritionPhoto : AI_KIND.nutrition);
      if (!allowance.canUse) {
        toast.warning(allowanceBlockedMessage(t, allowance), { duration: 5000 });
        return;
      }
      let estimate;
      try {
        if (mode === 'photo') {
          const { base64, mime } = await compressImage(photo);
          estimate = await getNutritionEstimateFromPhoto(mealType, base64, mime);
        } else {
          estimate = await getNutritionEstimate(mealType, text.trim());
        }
      } catch (e) {
        estimate = null;
      }
      if (!estimate) {
        toast.error(t('nutriAiError'));
        return;
      }
      // The server counted this use (no-food results only up to a hidden cap).
      if (!estimate.items.length) {
        await recordNutritionEstimateToHistory(user.id);
        toast.error(t('nutriAiNoFoodDetected'));
        return;
      }
      if (!estimate.mealType) estimate.mealType = mealType;
      if (mode === 'photo') {
        // Saved from the review modal (or dropped there).
        setReview(estimate);
        return;
      }
      await addAiEntry(user.id, logDate, estimate);
      await recordNutritionEstimateToHistory(user.id);
      onSaved(logDate);
    } catch (e) {
      toast.error(t('nutriAiError'));
    } finally {
      setBusy(false);
    }
  };

  const saveReviewed = async (estimate) => {
    try {
      await addAiEntry(user.id, logDate, estimate);
      await recordNutritionEstimateToHistory(user.id);
      onSaved(logDate);
    } catch (e) {
      toast.error(t('nutriAiError'));
    }
  };

  const discardReviewed = async () => {
    // The server already counted this use.
    await recordNutritionEstimateToHistory(user.id).catch(() => {});
    onClose();
  };

  const submitQuick = async () => {
    const n = (v) => Math.max(0, Math.round(Number(String(v).replace(',', '.')) || 0));
    if (!quick.label.trim() || n(quick.kcal) <= 0) {
      toast.warning(t('macroTargetsManualInvalidForm'));
      return;
    }
    setBusy(true);
    try {
      await addQuickAddEntry(user.id, logDate, { label: quick.label.trim(), caloriesKcal: n(quick.kcal), proteinG: n(quick.p), carbsG: n(quick.c), fatG: n(quick.f) });
      onSaved(logDate);
    } catch (e) {
      toast.error(t('anErrorOccurred'));
    } finally {
      setBusy(false);
    }
  };

  if (review) {
    return <MealPhotoReviewModal estimate={review} mealType={mealType} onSave={saveReviewed} onDiscard={discardReviewed} />;
  }

  return (
    <Modal
      open
      onClose={busy ? undefined : onClose}
      closeOnScrim={!busy}
      title={t('addMealMenuTitle')}
      footer={(
        <>
          <button type="button" className="gm-btn gm-btn--ghost" onClick={onClose} disabled={busy}>{t('cancel')}</button>
          <BusyButton busy={busy} disabled={mode === 'photo' && !user.isPremium} onClick={mode === 'manual' ? submitQuick : submitAi}>
            {busy && mode !== 'manual' ? t('estimating') : t('addEntry')}
          </BusyButton>
        </>
      )}
    >
      <div className="gm-segment" style={{ marginBottom: 16 }}>
        <button type="button" className={mode === 'text' ? 'active' : ''} onClick={() => setMode('text')}><Icon name="edit_note" size={18} /> {t('textMode')}</button>
        <button type="button" className={mode === 'photo' ? 'active' : ''} onClick={() => setMode('photo')}>
          <Icon name={user.isPremium ? 'photo_camera' : 'lock'} size={18} /> {t('photoMode')}
        </button>
        <button type="button" className={mode === 'manual' ? 'active' : ''} onClick={() => setMode('manual')}><Icon name="bolt" size={18} /> {t('quickAdd')}</button>
      </div>

      <div className="gm-grid-2 keep" style={{ marginBottom: 14 }}>
        {mode !== 'manual' ? (
          <div className="gm-field">
            <label>{t('mealType')}</label>
            <select className="gm-select" value={mealType} onChange={(e) => setMealType(e.target.value)}>
              {MEAL_TYPES.map((m) => <option key={m} value={m}>{t(m)}</option>)}
            </select>
          </div>
        ) : (
          <div className="gm-field">
            <label>{t('entryLabel')}</label>
            <input className="gm-input" value={quick.label} onChange={(e) => setQuick({ ...quick, label: e.target.value })} placeholder={t('quickAddLabel')} />
          </div>
        )}
        <div className="gm-field">
          <label>{t('date')}</label>
          <input className="gm-input" type="date" value={date} max={dateKey()} onChange={(e) => setDate(e.target.value || dateKey())} />
        </div>
      </div>

      {mode === 'text' && (
        <div className="gm-field">
          <label>{t('whatDidYouEat')}</label>
          <textarea className="gm-textarea" value={text} onChange={(e) => setText(e.target.value)} placeholder={t('enterMealDescription')} autoFocus />
          <span className="gm-tiny gm-muted">{t('addMealByTextSubtitle')}</span>
        </div>
      )}

      {mode === 'photo' && !user.isPremium && (
        <div className="gm-card" style={{ textAlign: 'center', padding: 22 }}>
          <Icon name="lock" size={36} style={{ color: 'var(--gm-premium-a)' }} />
          <p className="gm-strong" style={{ marginTop: 8 }}>{t('addMealByPhoto')}</p>
          <p className="gm-muted gm-small" style={{ marginTop: 4 }}>{t('addMealByPhotoPremiumTooltip')}</p>
          <button type="button" className="gm-btn gm-btn--premium" style={{ marginTop: 14 }} onClick={onPremium}>
            <Icon name="workspace_premium" /> {t('upgradeToPremium')}
          </button>
        </div>
      )}

      {mode === 'photo' && user.isPremium && (
        <div className="gm-field">
          <label>{t('mealPhotoSectionTitle')}</label>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => pickPhoto(e.target.files?.[0])} />
          {photoUrl ? (
            <div className="gm-stack-sm">
              <img src={photoUrl} alt="" className="gm-photo-preview" />
              <button type="button" className="gm-btn gm-btn--soft gm-btn--sm" onClick={() => fileRef.current?.click()}>
                <Icon name="refresh" size={18} /> {t('changePhoto')}
              </button>
            </div>
          ) : (
            <div
              className="gm-photo-drop"
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); pickPhoto(e.dataTransfer.files?.[0]); }}
            >
              <Icon name="add_a_photo" size={36} />
              <span className="gm-strong" style={{ color: 'var(--gm-text)' }}>{t('chooseFromGallery')}</span>
              <span className="gm-tiny">{t('addMealByPhotoSubtitle')}</span>
            </div>
          )}
        </div>
      )}

      {mode === 'manual' && (
        <div className="gm-grid-2 keep">
          <div className="gm-field"><label>{t('calories')} (kcal)</label><input className="gm-input" inputMode="numeric" value={quick.kcal} onChange={(e) => setQuick({ ...quick, kcal: e.target.value })} /></div>
          <div className="gm-field"><label>{t('protein')} (g)</label><input className="gm-input" inputMode="numeric" value={quick.p} onChange={(e) => setQuick({ ...quick, p: e.target.value })} /></div>
          <div className="gm-field"><label>{t('carbs')} (g)</label><input className="gm-input" inputMode="numeric" value={quick.c} onChange={(e) => setQuick({ ...quick, c: e.target.value })} /></div>
          <div className="gm-field"><label>{t('fat')} (g)</label><input className="gm-input" inputMode="numeric" value={quick.f} onChange={(e) => setQuick({ ...quick, f: e.target.value })} /></div>
        </div>
      )}

      {mode !== 'manual' && <AllowanceLine user={user} />}
    </Modal>
  );
}

/* ───────── Macro targets (AI) ───────── */
const GOALS = [['Muscle Gain', 'macroTargetsGoalMuscleGain'], ['Fat Loss', 'macroTargetsGoalFatLoss'], ['Maintenance', 'macroTargetsGoalMaintenance'], ['Weight Gain', 'macroTargetsGoalWeightGain']];
const LEVELS = [['sedentary', 'macroTargetsLevelSedentary'], ['light', 'macroTargetsLevelLight'], ['moderate', 'macroTargetsLevelModerate'], ['active', 'macroTargetsLevelActive'], ['very_active', 'macroTargetsLevelVeryActive']];
const WORKOUTS = [['strength', 'macroTargetsWorkoutStrength'], ['cardio', 'macroTargetsWorkoutCardio'], ['mixed', 'macroTargetsWorkoutMixed']];
const DIETS = [['balanced', 'macroTargetsDietBalanced'], ['low_carb', 'macroTargetsDietLowCarb'], ['high_protein', 'macroTargetsDietHighProtein']];

function MacroTargetsModal({ user, onClose }) {
  const t = useT();
  const toast = useToast();
  const { bodyDays } = useBodyDays(user.id);
  const { program } = useProgram(user.exerciseProgramID);
  const lastW = [...bodyDays].reverse().find((d) => d.weight_kg != null);
  const lastF = [...bodyDays].reverse().find((d) => d.body_fat_percent != null);
  const workoutDays = program ? Object.values(program).filter((l) => l.length).length : '';
  const [f, setF] = useState({
    sex: 'male', age: user.age ?? '', height: user.heightCm ?? '', weight: '', bf: '', goal: 'Muscle Gain', pace: 'sedentary', activity: 'sedentary', wk: '', workout: 'strength', diet: 'balanced',
  });
  const [busy, setBusy] = useState(false);
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current) return;
    if (lastW || lastF || workoutDays) {
      prefilled.current = true;
      setF((s) => ({
        ...s,
        weight: s.weight || (lastW ? Math.round(lastW.weight_kg) : ''),
        bf: s.bf || (lastF ? Math.round(lastF.body_fat_percent) : ''),
        wk: s.wk || workoutDays || '',
      }));
    }
  }, [lastW, lastF, workoutDays]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    const age = parseInt(f.age, 10);
    const height = parseInt(f.height, 10);
    const weight = parseInt(f.weight, 10);
    const bf = parseInt(f.bf, 10);
    const wk = parseInt(f.wk, 10);
    if ([age, height, weight, bf, wk].some((x) => Number.isNaN(x))) return toast.warning(t('macroTargetsInvalidForm'));
    if (age < 10 || age > 120) return toast.warning(t('macroTargetsInvalidAge'));
    if (height < 100 || height > 250) return toast.warning(t('macroTargetsInvalidHeight'));
    if (weight < 30 || weight > 300 || bf < 3 || bf > 60 || wk < 0 || wk > 14) return toast.warning(t('macroTargetsInvalidForm'));
    if (!navigator.onLine) return toast.error(t('macroTargetsInternetRequired'));
    const allowance = await getAiAllowance(user.id, AI_KIND.macroTargets);
    if (!allowance.canUse) return toast.warning(allowanceBlockedMessage(t, allowance));
    setBusy(true);
    try {
      const targets = await getMacroTargets({
        sex: f.sex, age, height_cm: height, weight_kg: weight, body_fat_percent: bf, goal: f.goal, pace: f.pace, activity_level: f.activity, workouts_per_week: wk, workout_type: f.workout, dietary_preference: f.diet,
      });
      if (!targets) throw new Error('empty');
      await updateMacroTargets(user.id, targets);
      await recordNutritionEstimateToHistory(user.id);
      await mergeProfileAgeHeightIfMissing(user.id, age, height);
      toast.success(t('macroTargetsSaved'));
      onClose();
    } catch (e) {
      toast.error(t('macroTargetsFetchError'));
    } finally {
      setBusy(false);
    }
    return null;
  };

  const select = (k, label, opts) => (
    <div className="gm-field">
      <label>{label}</label>
      <select className="gm-select" value={f[k]} onChange={set(k)}>
        {opts.map(([v, key]) => <option key={v} value={v}>{t(key)}</option>)}
      </select>
    </div>
  );
  const input = (k, label) => (
    <div className="gm-field">
      <label>{label}</label>
      <input className="gm-input" inputMode="numeric" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value.replace(/[^0-9]/g, '') })} />
    </div>
  );

  return (
    <Modal
      open
      wide
      onClose={busy ? undefined : onClose}
      title={t('macroTargetsSheetTitle')}
      footer={(
        <>
          <button type="button" className="gm-btn gm-btn--ghost" onClick={onClose} disabled={busy}>{t('cancel')}</button>
          <BusyButton busy={busy} className="gm-btn gm-btn--insight" onClick={submit}>{busy ? t('macroTargetsSubmitting') : t('macroTargetsSubmit')}</BusyButton>
        </>
      )}
    >
      <div className="gm-grid-3">
        {select('sex', t('macroTargetsSexLabel'), [['male', 'macroTargetsSexMale'], ['female', 'macroTargetsSexFemale']])}
        {input('age', t('macroTargetsAge'))}
        {input('height', t('macroTargetsHeightCm'))}
        {input('weight', t('macroTargetsWeightKg'))}
        {input('bf', t('macroTargetsBodyFatPercent'))}
        {select('goal', t('macroTargetsGoalLabel'), GOALS)}
        {select('pace', t('macroTargetsPaceLabel'), LEVELS)}
        {select('activity', t('macroTargetsActivityLevelLabel'), LEVELS)}
        {input('wk', t('macroTargetsWorkoutsPerWeek'))}
        {select('workout', t('macroTargetsWorkoutTypeLabel'), WORKOUTS)}
        {select('diet', t('macroTargetsDietaryPreferenceLabel'), DIETS)}
      </div>
    </Modal>
  );
}

function ManualTargetsModal({ user, current, onClose }) {
  const t = useT();
  const toast = useToast();
  const [v, setV] = useState({
    kcal: current?.daily_calories ?? '', p: current?.protein_g ?? '', c: current?.carbs_g ?? '', f: current?.fat_g ?? '',
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const n = (x) => parseInt(x, 10);
    const targets = { daily_calories: n(v.kcal), protein_g: n(v.p), carbs_g: n(v.c), fat_g: n(v.f) };
    if (Object.values(targets).some((x) => !(x > 0))) {
      toast.warning(t('macroTargetsManualInvalidForm'));
      return;
    }
    setBusy(true);
    try {
      await updateMacroTargets(user.id, targets);
      toast.success(t('macroTargetsManualSaved'));
      onClose();
    } catch (e) {
      toast.error(t('anErrorOccurred'));
    } finally {
      setBusy(false);
    }
  };
  const field = (k, label) => (
    <div className="gm-field">
      <label>{label}</label>
      <input className="gm-input" inputMode="numeric" value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value.replace(/[^0-9]/g, '') })} />
    </div>
  );
  return (
    <Modal
      open
      onClose={onClose}
      title={t('macroTargetsManualSheetTitle')}
      footer={(
        <>
          <button type="button" className="gm-btn gm-btn--ghost" onClick={onClose}>{t('cancel')}</button>
          <BusyButton busy={busy} onClick={save}>{t('macroTargetsManualSave')}</BusyButton>
        </>
      )}
    >
      <div className="gm-grid-2 keep">
        {field('kcal', `${t('calories')} (kcal)`)}
        {field('p', `${t('protein')} (g)`)}
        {field('c', `${t('carbs')} (g)`)}
        {field('f', `${t('fat')} (g)`)}
      </div>
    </Modal>
  );
}

/* ───────── Page ───────── */
export function goalsFrom(user) {
  const m = user?.macro_targets;
  const valid = m && m.daily_calories > 0 && m.protein_g > 0 && m.carbs_g > 0 && m.fat_g > 0;
  return {
    targets: valid ? m : null,
    goals: valid
      ? { protein: Math.round(m.protein_g), carbs: Math.round(m.carbs_g), fat: Math.round(m.fat_g), calories: Math.round(m.daily_calories) }
      : DEFAULT_GOALS,
  };
}

export default function Nutrition() {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const { openPremium } = useOutletContext() || {};
  const { user } = useSession();
  const today = useToday();
  const [weekly, setWeekly] = useState(false);
  const [modal, setModal] = useState(null);
  const [detail, setDetail] = useState(null);
  const [hidden, setHidden] = useState(() => new Set());
  const celebrate = useCelebrate();
  const { data: todayDoc } = useDoc(nutritionDayRef(user.id, dateKey(today)));
  const { data: waterDoc } = useDoc(waterDayRef(user.id, dateKey(today)));
  const { nutritionDays } = useNutritionDays(weekly ? user.id : null);
  const { waterByDate } = useWaterDays(weekly ? user.id : null);
  const { targets, goals } = goalsFrom(user);

  const entries = (todayDoc?.entries || []).filter((e) => !hidden.has(e.id));
  const totals = macrosOf(todayDoc?.day_totals);

  const week = useMemo(() => {
    const keys = weekDates(today).map(dateKey);
    const byDate = Object.fromEntries(nutritionDays.map((d) => [d.date || d.id, macrosOf(d.day_totals)]));
    const values = keys.map((k) => byDate[k]?.calories_kcal || 0);
    const sum = keys.reduce((acc, k) => {
      const m = byDate[k];
      if (!m) return acc;
      return { calories_kcal: acc.calories_kcal + m.calories_kcal, protein_g: acc.protein_g + m.protein_g, carbs_g: acc.carbs_g + m.carbs_g, fat_g: acc.fat_g + m.fat_g };
    }, { calories_kcal: 0, protein_g: 0, carbs_g: 0, fat_g: 0 });
    return { values, sum };
  }, [nutritionDays, today]);

  const remove = async (entry) => {
    setHidden((s) => new Set(s).add(entry.id));
    try {
      await deleteNutritionEntry(user.id, today, entry.id);
      toast.show(t('mealDeleted'), {
        duration: 5000,
        action: { label: t.upper(t('undo')), onClick: () => restoreNutritionEntry(user.id, today, entry).catch(() => {}) },
      });
    } catch (e) {
      toast.error(t('anErrorOccurred'));
    } finally {
      setHidden((s) => {
        const n = new Set(s);
        n.delete(entry.id);
        return n;
      });
    }
  };

  // Every meal, like the app (it used to be the day's first one only).
  const onSaved = () => {
    setModal(null);
    celebrate({ emoji: '🥗', color: '#00C896', message: t('nutritionStreakLoggedToday') });
  };

  return (
    <div className="gm-content gm-enter" style={{ paddingBottom: 220 }}>
      <div className="gm-page-head">
        <h1 className="gm-page-title">{t.upper(t('nutriAiTab'))}</h1>
      </div>
      <div style={{ maxWidth: 520, marginBottom: 16 }}>
        <PeriodTabs weekly={weekly} onChange={setWeekly} />
      </div>

      {weekly ? (
        <div className="gm-grid-2">
          <WeeklyCaloriesCard values={week.values} target={targets?.daily_calories} today={today} />
          <div className="gm-stack">
            <div>
              <div className="gm-upper-sm gm-muted" style={{ marginBottom: 8 }}>{t.upper(t('weeklyTotals'))}</div>
              <MacroRings totals={week.sum} goals={{ protein: goals.protein * 7, carbs: goals.carbs * 7, fat: goals.fat * 7 }} />
            </div>
            <WeeklyWaterCard waterByDate={waterByDate} goal={user.waterGoalMl} today={today} />
          </div>
        </div>
      ) : (
        <div className="gm-dash">
          <div className="gm-stack">
            <CalorieFlameBar current={totals.calories_kcal} goal={goals.calories} />
            <MacroRings totals={totals} goals={goals} />
            <WaterTrackerCard uid={user.id} ml={Math.max(0, Number(waterDoc?.ml) || 0)} goal={user.waterGoalMl} />
            <div className="gm-row gm-wrap">
              <button type="button" className="gm-btn gm-btn--insight gm-grow" onClick={() => setModal('targets')}>
                <Icon name="track_changes" /> {t('macroTargetsButton')}
              </button>
              <button type="button" className="gm-btn gm-btn--outline gm-grow" onClick={() => setModal('manual')}>
                <Icon name="edit" outline /> {t('macroTargetsManualButton')}
              </button>
            </div>
          </div>
          <div>
            <div className="gm-row-between" style={{ marginBottom: 10 }}>
              <h2 className="gm-section-title" style={{ margin: 0, fontSize: 16, letterSpacing: 0.6 }}>{t.upper(t('todayMeals'))}</h2>
              <span className="gm-pill">{entries.length}</span>
            </div>
            {entries.length === 0 ? (
              <div className="gm-card">
                <Empty icon="restaurant">{t('enterMealDescription')}</Empty>
              </div>
            ) : (
              <div className="gm-stack-sm">
                {entries.map((e) => <MealCard key={e.id} entry={e} onOpen={() => setDetail(e)} onDelete={() => remove(e)} />)}
              </div>
            )}
            <button type="button" className="gm-btn gm-btn--block" style={{ marginTop: 16 }} onClick={() => navigate('/nutrition/records')}>
              <Icon name="history" /> {t('allRecords')}
            </button>
          </div>
        </div>
      )}

      <div className="gm-fabs">
        <button type="button" className="gm-fab" onClick={() => setModal('add')} title={t('addMealMenuTitle')}>
          <Icon name="add" /><span className="gm-fab__label">{t('addMealMenuTitle')}</span>
        </button>
      </div>

      {modal === 'add' && (
        <AddMealModal user={user} onClose={() => setModal(null)} onSaved={onSaved} onPremium={() => { setModal(null); openPremium?.(); }} />
      )}
      {modal === 'targets' && <MacroTargetsModal user={user} onClose={() => setModal(null)} />}
      {modal === 'manual' && <ManualTargetsModal user={user} current={targets} onClose={() => setModal(null)} />}
      <MealDetailModal entry={detail} dateStr={formatDate(today, t.locale)} onClose={() => setDetail(null)} />
    </div>
  );
}
